import { editResponseMessageHandler } from '../editMessageHandlers';
import { ViewType } from 'owa-calendar-actions';
import { canProposeTime, canRespond, isSeriesInstance } from 'owa-calendar-event-capabilities';
import CalendarTime from 'owa-calendar-types/lib/types/CalendarTime';
import { CalendarEvent } from 'owa-calendar-types';
import { ControlIcons } from 'owa-control-icons';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { acceptButtonText } from 'owa-locstrings/lib/strings/acceptbuttontext.locstring.json';
import { declineButtonText } from 'owa-locstrings/lib/strings/declinebuttontext.locstring.json';
import { tentativeButtonText } from 'owa-locstrings/lib/strings/tentativebuttontext.locstring.json';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import EventScope from 'owa-service/lib/contract/EventScope';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { IContextualMenuItem, ContextualMenuItemType } from '@fluentui/react/lib/ContextualMenu';
import {
    getCaptionFromItemResponseType,
    getIconFromItemResponseType,
    hasResponded,
    getMeetingResponseMenuItems,
} from 'owa-event-context-menu';

/**
 * Returns the ATD buttons (or button when responded)
 * @param event calendar event
 * @param actionSource Calendar view
 * @param onRespondClicked onRespondClicked callback
 * @param getProposeNewTimeButton the Propose New Time Button for the Read Form
 */
export const getMeetingResponseButton = (
    event: CalendarEvent,
    actionSource: ViewType,
    onRespondClicked: (
        newResponseType: ResponseTypeType,
        eventScope: EventScope,
        sendMessage: boolean,
        notes: string
    ) => void,
    getProposeNewTimeButton?: (event: CalendarEvent) => FilterableMenuItem,
    onMenuOpened?: () => void,
    onSendHandler?: (
        eventScope: EventScope,
        responseText: string,
        proposedTime: CalendarTime,
        sendResponse: boolean,
        forwardees: AttendeeType[],
        timeOpen: number
    ) => void
): FilterableMenuItem[] => {
    if (!canRespond(event)) {
        // If we cannot respond, no ATD buttons
        return [];
    }

    const responseMenu = (responseType: ResponseTypeType) =>
        getMeetingResponseMenuItems(
            event,
            responseType,
            createOnResponseClicked(event, onRespondClicked, actionSource, onSendHandler)
        );

    if (!hasResponded(event)) {
        let menus = [];

        menus.push(
            new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(acceptButtonText),
                [FilterableMenuItemArgs.icon]: ControlIcons.Accept,
                [FilterableMenuItemArgs.onClick]: isSeriesInstance(event)
                    ? null
                    : responseMenu('Accept')[0].onClick,
                [FilterableMenuItemArgs.menuContent]: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Accept'),
                          onMenuOpened: onMenuOpened,
                      }
                    : null,
                [FilterableMenuItemArgs.key]: 'Accept',
            }),
            new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(tentativeButtonText),
                [FilterableMenuItemArgs.icon]: ControlIcons.Help,
                [FilterableMenuItemArgs.onClick]: isSeriesInstance(event)
                    ? null
                    : responseMenu('Tentative')[0].onClick,
                [FilterableMenuItemArgs.menuContent]: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Tentative'),
                          onMenuOpened: onMenuOpened,
                      }
                    : null,
                [FilterableMenuItemArgs.key]: 'Tentative',
            }),
            new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(declineButtonText),
                [FilterableMenuItemArgs.icon]: ControlIcons.Cancel,
                [FilterableMenuItemArgs.onClick]: isSeriesInstance(event)
                    ? null
                    : responseMenu('Decline')[0].onClick,
                [FilterableMenuItemArgs.menuContent]: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Decline'),
                          onMenuOpened: onMenuOpened,
                      }
                    : null,
                [FilterableMenuItemArgs.key]: 'Decline',
            })
        );
        if (actionSource === 'ReadForm') {
            menus.push(getProposeNewTimeButton(event));
        }

        return menus;
    } else {
        let subMenuItems = [];

        subMenuItems.push(
            {
                key: 'Accept',
                name: loc(acceptButtonText),
                iconProps: { iconName: ControlIcons.Accept },
                subMenuProps: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Accept'),
                          onMenuOpened: onMenuOpened,
                      }
                    : undefined,
                onClick: isSeriesInstance(event) ? undefined : responseMenu('Accept')[0].onClick,
            },
            {
                key: 'Tentative',
                name: loc(tentativeButtonText),
                iconProps: { iconName: ControlIcons.Help },
                subMenuProps: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Tentative'),
                          onMenuOpened: onMenuOpened,
                      }
                    : undefined,
                onClick: isSeriesInstance(event) ? undefined : responseMenu('Tentative')[0].onClick,
            },
            {
                key: 'Decline',
                name: loc(declineButtonText),
                iconProps: { iconName: ControlIcons.Cancel },
                subMenuProps: isSeriesInstance(event)
                    ? {
                          items: responseMenu('Decline'),
                          onMenuOpened: onMenuOpened,
                      }
                    : undefined,
                onClick: isSeriesInstance(event) ? undefined : responseMenu('Decline')[0].onClick,
            }
        );

        if (actionSource === 'ReadForm' && canProposeTime(event)) {
            let proposeMenu = getProposeNewTimeButton(event);
            proposeMenu.iconProps = {
                iconName: ControlIcons.DateTime,
            };

            const proposeMenuDivider = {
                key: 'proposemenu_divider',
                itemType: ContextualMenuItemType.Divider,
            };

            subMenuItems.push(proposeMenuDivider as IContextualMenuItem);
            subMenuItems.push(proposeMenu as IContextualMenuItem);
        }

        let onClick;
        let menuItems;

        if (subMenuItems.length === 0) {
            onClick = responseMenu(null)[0].onClick;
        } else {
            menuItems = { items: subMenuItems, onMenuOpened: onMenuOpened };
        }

        return [
            new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: getCaptionFromItemResponseType(event.ResponseType),
                [FilterableMenuItemArgs.icon]: getIconFromItemResponseType(event.ResponseType),
                [FilterableMenuItemArgs.onClick]: onClick,
                [FilterableMenuItemArgs.menuContent]: menuItems,
                [FilterableMenuItemArgs.key]: 'ResponseTypeMenu',
            }),
        ];
    }
};

function createOnResponseClicked(
    item: CalendarEvent,
    onRespondClicked: (
        newResponseType: ResponseTypeType,
        eventScope: EventScope,
        sendMessage: boolean,
        notes: string
    ) => void,
    actionSource: ViewType,
    onSendHandler?: (
        eventScope: EventScope,
        responseText: string,
        proposedTime: CalendarTime,
        sendResponse: boolean,
        forwardees: AttendeeType[],
        timeOpen: number
    ) => void
): (responseType: ResponseTypeType, eventScope: EventScope, editorTitleText: string) => void {
    return (responseType: ResponseTypeType, eventScope: EventScope, editorTitleText: string) => {
        if (!item.IsResponseRequested) {
            // if response is not requested, respond directly
            onRespondClicked(responseType, eventScope, false /* sendMessage */, null /** notes */);
        } else {
            editResponseMessageHandler(
                item.ItemId,
                item.ResponseType,
                item.FreeBusyType,
                eventScope,
                item.IsRecurring,
                editorTitleText,
                actionSource,
                responseType,
                null, // Proposed New Time
                item.IsResponseRequested,
                null, // meetingMessageId
                onSendHandler
            );
        }
    };
}
